<template>
  <div :class="{ 'px-4': isDetailsSlideout }">
    <v-row v-if="loading">
      <v-col>
        <Loader
          text="Loading updates"
          color="loaderColor"
          size="small"
        ></Loader>
      </v-col>
    </v-row>
    <v-row
      v-if="!loading && isDetailsSlideout && (!updates || !updates.length)"
    >
      <v-col cols="12" class="pt-3 content-container">
        No analysis updates from the last month
      </v-col>
    </v-row>
    <v-row v-if="!loading && updates && updates.length">
      <v-col
        cols="12"
        class="pt-3 content-container"
        :class="{ 'content-container-details': isDetailsSlideout }"
      >
        <div>
          <span
            v-if="!isDetailsSlideout"
            class="details-heading application-heading pull-left mr-3"
            >{{ labels.cr_updates }}</span
          >
          <span
            class="pull-left last-30-days-text"
            :class="{ 'application-heading': isDetailsSlideout }"
            >Last month</span
          >

          <v-btn
            class="pull-left ml-3 copy-btn"
            outlined
            x-small
            @click="handleCopyClick"
          >
            <v-icon class="mr-2 left" size="12">far fa-clipboard</v-icon>
            Copy
          </v-btn>

          <v-btn-toggle v-model="sortBy" borderless class="pull-right">
            <v-btn value="newest" x-small>
              <span class="hidden-sm-and-down">Newest</span>
            </v-btn>
            <v-btn value="oldest" x-small>
              <span class="hidden-sm-and-down">Oldest</span>
            </v-btn>
          </v-btn-toggle>
          <div class="clearfix"></div>
        </div>

        <v-card
          :class="{
            'mt-2 pa-3': !isDetailsSlideout,
            'py-3 px-0': isDetailsSlideout,
          }"
        >
          <div v-for="update in updates" :key="update.id" class="mb-3">
            <p
              class="mb-0 update-title"
              :class="{
                'text-highlight application-heading': !isDetailsSlideout,
              }"
              @click="handleClickUpdate(update)"
            >
              {{ update.title }}
            </p>
            <em class="update-date">{{ formatDate(update.liveDate) }}</em>
            <div
              v-if="update.showBody"
              ref="updates"
              v-html="getCleanBody(update.body)"
              class="html-body"
            ></div>
          </div>
        </v-card>
      </v-col>
      <v-overlay :absolute="true" :value="screenShotting">
        <div class="copy-overlay-indicator">
          <v-icon size="50" color="white">fas fa-check</v-icon>
          <div><strong>Copied</strong></div>
        </div>
      </v-overlay>
    </v-row>
  </div>
</template>

<script>
import lodashOrderBy from "lodash/orderBy";
import lodashCloneDeep from "lodash/cloneDeep";

import { getCountryUpdateArticles } from "@/services/controlRisks/risks.service";
import { getFormatted } from "@/common/date.utilities";
import { addTargetToLinks } from "@/common/basic.utilities";
import { LABELS } from "@/common/constants/labels";
import { embedInfoText } from "@/common/basic.utilities";
import { dataToCopy } from "@/services/search/wod.service";
import { getCountryByCode } from "@/common/maps/utilities";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

export default {
  name: "RisksUpdates",
  components: {
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    iso3Code: {
      type: String,
      required: true,
    },
    category: {
      type: Object,
    },
    isDetailsSlideout: {
      type: Boolean,
    },
  },
  data() {
    return {
      screenShotting: false,
      updates: null,
      sortBy: "newest",
      loading: false,
      labels: LABELS,
    };
  },
  computed: {
    loaderColor() {
      return this.isDetailsSlideout ? "white" : "black";
    },
  },
  methods: {
    async getUpdates() {
      this.loading = true;
      if (this.iso3Code) {
        this.updates = await getCountryUpdateArticles(
          this.iso3Code,
          this.category
        );
        if (this.updates) {
          this.sortUpdates();
          this.updates.forEach((update) => {
            this.$set(update, "showBody", false);
          });
        }
      }
      this.loading = false;
    },
    sortUpdates() {
      const sortDir = this.sortBy === "newest" ? "desc" : "asc";
      this.updates = lodashOrderBy(this.updates, ["liveDate"], [sortDir]);
    },
    getCleanBody(body) {
      if (body) {
        return addTargetToLinks(body);
      }
      return "";
    },
    formatDate(inDate) {
      if (inDate) {
        return getFormatted(inDate);
      }
      return null;
    },
    handleClickUpdate(update) {
      update.showBody = !update.showBody;

      // add click event to all the images to open them in separate tab
      this.$nextTick(() => {
        const elements = this.$refs.updates;
        if (elements && Array.isArray(elements)) {
          elements.forEach((elem) => {
            const imgs = elem.querySelectorAll("img");
            Array.from(imgs).forEach((img) => {
              img.style.cursor = "pointer";
              img.addEventListener("click", () => {
                const w = window.open("", "_blank");
                w.document.write(img.outerHTML);
              });
            });

            embedInfoText(
              elem,
              "Show source info",
              `Seerist's analytical updates are produced by a team of over 100 analysts at Control Risks. These analysts are recognized country and subject-matter experts, and are based in the countries or regions that they cover. Our analyst team uses a proprietary risk rating and analytical methodology to assess country risks and ensure global consistency, and all updates undergo a quality review process. Analysts will typically only write an update if an event is being reported by multiple credible sources. However, analysts will consider a variety of factors in their analysis - including historical knowledge, in-country experiences, conversations with contacts on the ground, and experience advising clients - particularly when assessing the likelihood of a potential event or providing an outlook for a risk area or issue.`
            );
          });
        }
      });
    },
    async handleCopyClick() {
      let items = lodashCloneDeep(this.updates);

      const colsToCopy = [
        "title",
        "liveDate",
        "country",
        "riskCategories",
        "body",
      ];

      const country = JSON.parse(await getCountryByCode(this.iso3Code)).data
        .properties.officialName;

      items.forEach((value, index) => {
        value.country = country;
      });

      document.activeElement.blur();

      this.screenShotting = true;

      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.analysis,
        EVENT_ACTIONS.clicked,
        "Copy To Clipboard"
      );

      await dataToCopy(items, colsToCopy, false);

      setTimeout(() => {
        this.screenShotting = false;
      }, 500);
    },
  },
  watch: {
    iso3Code: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.getUpdates();
        }
      },
      immediate: true,
    },
    category: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.getUpdates();
        }
      },
    },
    sortBy: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.sortUpdates();
        }
      },
    },
  },
  beforeDestroy() {},
};
</script>

<style>
.content-container-details img {
  max-width: 320px !important;
}
</style>

<style scoped>
.details-heading {
  font-size: 20px;
}
.content-container {
  max-width: 530px !important;
  font-size: 14px;
}
.update-title {
  font-size: 14px;
  cursor: pointer;
}
.update-date {
  font-size: 12px;
}
.last-30-days-text {
  position: relative;
  top: 3px;
}
.last-30-days-text-bold {
  font-weight: bold;
  font-family: TruenoRg;
}

.copy-btn {
  position: relative;
  top: 2px;
}
.v-overlay {
  left: 0px !important;
  background-color: rgba(0, 0, 0, 0) !important;
}

.v-overlay--active.v-overlay {
  background-color: rgba(0, 0, 0, 0.75) !important;
}

.copy-overlay-indicator {
  width: 120px;
  height: 120px;
  background-color: green;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  font-size: large;
}
</style>
